import React, { useCallback, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider, useMutation } from '@tanstack/react-query';
import { MoodWidget } from '@traquility/ui/diary';
import { FirstEntryCelebrationModal } from '@traquility/ui/diary';

// Create a query client for this component tree
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
});

interface MoodWidgetContainerProps {
  /** Current mood value (0-100) or null */
  mood: number | null;
  /** Diary date for API calls (YYYY-MM-DD) */
  diaryDate: string;
  /** GraphQL endpoint URL */
  graphqlEndpoint: string;
  /** CSRF token for API requests */
  csrfToken: string;
  /** Translations for labels and modal */
  translations: {
    labels: Record<string, string>;
    dayLabel: string;
    celebrationTitle: string;
    celebrationSubtitle: string;
    celebrationMessage: string;
    celebrationHint: string;
    celebrationCta: string;
  };
}

// GraphQL mutation for updating mood
const UPDATE_DIARY_DAY_MOOD = `
  mutation UpdateDiaryDayMood($diaryDate: String!, $mood: Int!) {
    updateDiaryDay(input: { diaryDate: $diaryDate, mood: $mood }) {
      diaryDay {
        id
        mood
      }
      showFirstEntryCelebration
    }
  }
`;

function MoodWidgetInner({
  mood,
  diaryDate,
  graphqlEndpoint,
  csrfToken,
  translations,
}: MoodWidgetContainerProps) {
  const [currentMood, setCurrentMood] = useState<number | null>(mood);
  const [showCelebration, setShowCelebration] = useState<boolean>(false);

  // Mutation for updating mood via GraphQL
  const mutation = useMutation({
    mutationFn: async (newMood: number) => {
      const response = await fetch(graphqlEndpoint, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          query: UPDATE_DIARY_DAY_MOOD,
          variables: {
            diaryDate,
            mood: newMood,
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update mood');
      }

      const result = await response.json();
      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'GraphQL error');
      }

      return result.data.updateDiaryDay;
    },
    onSuccess: (data, variables) => {
      // Update local state on success
      setCurrentMood(variables);

      // Show celebration modal if this was first entry
      if (data.showFirstEntryCelebration) {
        setShowCelebration(true);
      }
    },
  });

  const handleMoodChange = useCallback(
    async (newMood: number): Promise<{ showCelebration: boolean }> => {
      // Optimistically update the local state
      setCurrentMood(newMood);

      // Send the mutation
      const result = await mutation.mutateAsync(newMood);
      return { showCelebration: result.showFirstEntryCelebration };
    },
    [mutation]
  );

  const handleCloseCelebration = useCallback(() => {
    setShowCelebration(false);
  }, []);

  return (
    <>
      <MoodWidget
        mood={currentMood}
        onMoodChange={handleMoodChange}
        translations={{
          labels: translations.labels,
        }}
        isLoading={mutation.isPending}
      />

      <FirstEntryCelebrationModal
        isOpen={showCelebration}
        onClose={handleCloseCelebration}
        translations={{
          title: translations.celebrationTitle,
          subtitle: translations.celebrationSubtitle,
          message: translations.celebrationMessage,
          hint: translations.celebrationHint,
          cta: translations.celebrationCta,
          dayLabel: translations.dayLabel,
        }}
      />
    </>
  );
}

/**
 * MoodWidgetContainer - Entry point for rendering mood widget in Rails
 *
 * This component wraps the shared MoodWidget with:
 * - React Query provider
 * - GraphQL mutation handling
 * - CSRF token handling for Rails
 * - Celebration modal integration
 */
export function MoodWidgetContainer(props: MoodWidgetContainerProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <MoodWidgetInner {...props} />
    </QueryClientProvider>
  );
}

/**
 * Mount function for rendering from Rails
 */
export function mountMoodWidget(
  container: HTMLElement,
  props: MoodWidgetContainerProps
) {
  const root = createRoot(container);
  root.render(<MoodWidgetContainer {...props} />);
  return root;
}

export default MoodWidgetContainer;
